import { useEffect, useRef, useState, type ReactNode } from "react";
import { ArrowLeft, Download, Languages, LogOut, UserRound } from "lucide-react";
import { useNavigate } from "react-router";
import { useAuth } from "@/auth/AuthProvider";
import { useTenant } from "@/tenant/TenantProvider";
import { useLanguage } from "@/i18n/language";
import { LanguageSwitch } from "@/i18n/LanguageSwitch";
import { KarigarBottomNav, type KarigarNavKey } from "./KarigarBottomNav";

/**
 * The frame every karigar screen sits in — a branded header band, one
 * scrolling region, fixed bottom nav.
 *
 * The header is the org's sign-in panel colour (`--gq-panel`, derived from
 * their brand) with the QR module texture, and the screen's first card rides
 * up over its lower edge. That overlap is the app's signature: the karigar
 * opens it and sees their brand and their balance in one glance.
 *
 * The header scrolls away with the content rather than staying pinned — on a
 * small phone every row of height is worth more to the list than to a title
 * the person already read. Only the bottom nav is fixed.
 *
 * The `h-dvh` wrapper is self-sufficient: these routes mount without
 * `AppShell`, so nothing above supplies an explicit height.
 */
export function KarigarShell({
  active,
  title,
  children,
}: {
  active: KarigarNavKey;
  title: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex h-dvh flex-col bg-surface">
      {/* `pb-28` clears the fixed nav, which is positioned against the
          viewport and so cannot push content up. */}
      <div className="min-h-0 flex-1 overflow-y-auto pb-28">
        <KarigarHeader title={title} showBack={active !== "home"} />
        <div className="relative mx-auto -mt-10 flex max-w-md flex-col gap-4 px-4 gq-stagger">
          {children}
        </div>
      </div>

      <KarigarBottomNav active={active} />
    </div>
  );
}

/**
 * The branded band. Exported so screens outside the shell can match it.
 *
 * Signed in, its corner holds the profile button, where the language is
 * chosen. On the public screens nobody is signed in yet, so the language
 * switch sits there directly — a karigar must be able to pick English before
 * they have an account.
 */
export function KarigarHeader({ title, showBack = false }: { title: ReactNode; showBack?: boolean }) {
  const { payload } = useTenant();
  const { user } = useAuth();
  const navigate = useNavigate();
  const brand = payload.branding;
  // Inheriting the platform mark would put GatiQ's logo on a karigar's
  // screen for a brand they have never heard of.
  const logo = payload.hasOwnLogo ? brand.logoDark || brand.logo : null;

  return (
    <header className="relative bg-panel px-4 pt-[max(1rem,env(safe-area-inset-top))] pb-14 text-panel-text">
      {/* Decoration is clipped here, not on the header, so the profile menu
          can drop below the band's edge. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="gq-modules absolute inset-0 opacity-[0.07]" />
        <div className="absolute -top-24 -right-16 size-64 rounded-full bg-brand/25 blur-[70px]" />
      </div>
      <div className="relative mx-auto flex max-w-md flex-col gap-3">
        <div className="flex h-8 items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {showBack ? (
              <button
                aria-label="Back"
                className="grid size-9 place-items-center rounded-full text-panel-text transition-colors hover:bg-panel-text/10"
                onClick={() => navigate(-1)}
                type="button"
              >
                <ArrowLeft aria-hidden="true" className="size-5" />
              </button>
            ) : null}
            {logo ? (
              <img alt={brand.appName} className="h-6 max-w-[8rem] object-contain" src={logo} />
            ) : (
              <p className="font-title text-sm font-semibold tracking-[-0.01em] text-panel-muted">
                {brand.appName}
              </p>
            )}
          </div>
          {user ? <ProfileMenu /> : <LanguageSwitch fallback="hi" tone="panel" />}
        </div>
        <h1 className="font-title text-xl leading-snug font-semibold tracking-[-0.015em] text-panel-text">
          {title}
        </h1>
      </div>
    </header>
  );
}

function ProfileMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage("hi");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!user) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={t("प्रोफ़ाइल", "Profile")}
        className="grid size-9 place-items-center rounded-full bg-panel-text/10 text-panel-text ring-1 ring-panel-text/20 transition-colors hover:bg-panel-text/20"
        onClick={() => setOpen((o) => !o)}
        type="button"
      >
        <UserRound aria-hidden="true" className="size-[1.125rem]" />
      </button>

      {open ? (
        <div
          className="absolute right-0 z-30 mt-2 w-64 origin-top-right animate-pop rounded-lg border border-rule bg-raised p-1.5 text-ink shadow-pop"
          role="menu"
        >
          <div className="rounded-md bg-ink/[0.03] px-3 py-2.5">
            <p className="truncate text-sm font-medium text-ink">{user.name}</p>
            <p className="truncate text-xs text-muted">
              {user.phone}
              {user.displayId ? ` · ${user.displayId}` : ""}
            </p>
          </div>
          <div className="flex flex-col gap-2 px-3 pt-3 pb-2">
            <span className="flex items-center gap-2 text-sm text-ink">
              <Languages aria-hidden="true" className="size-4 text-muted" />
              {t("भाषा", "Language")}
            </span>
            <LanguageSwitch fallback="hi" />
          </div>
          {/* The karigar's route to the install page. Their bottom nav is four
              fixed tabs with no room for a fifth, and this menu is the only
              place a karigar navigates from — so install lives beside the
              language they already come here to change. */}
          <button
            className="mt-1 flex w-full items-center gap-2 rounded-md px-3 py-2.5 text-sm font-medium text-ink hover:bg-ink/[0.05]"
            onClick={() => {
              setOpen(false);
              const slug = user.orgSlug;
              if (slug) navigate(`/${slug}/karigar/install`);
            }}
            role="menuitem"
            type="button"
          >
            <Download aria-hidden="true" className="size-4 text-muted" />
            {t("ऐप इंस्टॉल करें", "Install app")}
          </button>
          <button
            className="mt-1 flex w-full items-center gap-2 rounded-md px-3 py-2.5 text-sm font-medium text-ink hover:bg-ink/[0.05]"
            onClick={() => {
              const slug = user.orgSlug;
              void logout().then(() => navigate(slug ? `/${slug}/login/karigar` : "/login", { replace: true }));
            }}
            role="menuitem"
            type="button"
          >
            <LogOut aria-hidden="true" className="size-4 text-muted" />
            {t("लॉग आउट", "Sign out")}
          </button>
        </div>
      ) : null}
    </div>
  );
}
